// ============================================================
// FICHIER : RoutesMapUS.tsx
// Dashboard "US Routes status vs 2000" — carte 2D interactive (PAS le
// moteur 3D de MobilityMarketUS), zoom/pan Leaflet classiques, tuiles
// CARTO dark_all, routes affichées comme des lignes colorées par statut
// (route_type) — alimenté par la table us_routes_table_evolution.
//
// NOTE : le nom exact de la colonne géométrie n'a pas été confirmé
// (voir getRouteGeometry() dans fetchLayers.us.ts) — si les routes ne
// s'affichent pas une fois connecté en direct, c'est le premier endroit
// à vérifier.
// ============================================================

import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  fetchUSRoutes,
  fetchUSRouteStatuses,
  getRouteGeometry,
  USRouteFilters,
} from "../layers/fetchLayers.us";
import MapLoadingOverlay from "./MapLoadingOverlay";
import "./RoutesMapUS.css";
import { cartoBasemapUrl } from "../config/carto";

// Couleurs par statut — le rouge ATR pour les routes fermées est la
// priorité narrative de cette section ("800 routes closed since 2000").
// Les autres statuts retombent sur une palette neutre si route_type
// contient d'autres valeurs que "closed"/"active"/"new".
function colorForStatus(status: string): string {
  const s = (status || "").toLowerCase();
  if (s.includes("clos")) return "#EB280F"; // rouge ATR — routes fermées
  if (s.includes("new") || s.includes("open")) return "#56C177"; // vert — nouvelles routes
  if (s.includes("activ")) return "#009A84"; // teal — routes actives/stables
  return "#99D6EA"; // bleu clair — statut inconnu, par défaut
}

export interface RoutesMapUSHandle {
  resetView: () => void;
}

interface Props {
  filters: USRouteFilters;
  onReady?: (handle: RoutesMapUSHandle) => void;
  onStats?: (stats: { routeCount: number; loading: boolean }) => void;
}

const DEFAULT_CENTER: [number, number] = [39.0, -100.0];
const DEFAULT_ZOOM = window.innerHeight <= 800 ? 3 : 4;

export default function RoutesMapUS({ filters, onReady, onStats }: Props) {
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mapRef.current) return;
    const map = L.map("routes-map-us", {
      center: DEFAULT_CENTER,
      zoom: 4,
      zoomControl: false,
      scrollWheelZoom: true,
      attributionControl: false,
      preferCanvas: true,
    });


 L.tileLayer(cartoBasemapUrl()).addTo(map);

mapRef.current = map;
layerRef.current = L.layerGroup().addTo(map);

map.whenReady(() => {
  requestAnimationFrame(() => {
    map.invalidateSize();

    const isLaptop =
      window.innerWidth >= 901 &&
      window.innerHeight <= 900;

    if (isLaptop && mapRef.current === map) {
      map.panBy([-220, 0], {
        animate: false,
      });
    }
  });
});

if (onReady) {
  onReady({
    resetView: () =>
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM, { animate: true }),
  });
}

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;

    setLoading(true);
    setError(null);
    onStats?.({ routeCount: 0, loading: true });
    layer.clearLayers();

    fetchUSRoutes(filters)
      .then((rows) => {
        // ── Diagnostic — affiche la forme RÉELLE de la 1ère ligne renvoyée
        // par Databricks dans la console. On a déjà deviné le format de
        // géométrie deux fois (JSON puis WKT BigQuery) sans jamais voir la
        // vraie donnée — ce log permet de la voir enfin et d'ajuster
        // getRouteGeometry() précisément plutôt que de re-deviner. ──
        if (rows.length > 0) {
          // eslint-disable-next-line no-console
          console.log("[RoutesMapUS] première ligne brute reçue :", JSON.stringify(rows[0]));
          // eslint-disable-next-line no-console
          console.log("[RoutesMapUS] clés disponibles (" + Object.keys(rows[0]).length + ") : " + Object.keys(rows[0]).join(", "));
          // eslint-disable-next-line no-console
          console.log("[RoutesMapUS] --- valeur de CHAQUE colonne pour la 1ère ligne ---");
          Object.entries(rows[0]).forEach(([k, v]) => {
            // eslint-disable-next-line no-console
            console.log(`[RoutesMapUS]   ${k} (${typeof v}) = ${JSON.stringify(v)?.slice(0, 200)}`);
          });
          const rawGeom = rows[0].line_geom ?? rows[0].geom ?? rows[0].geometry ?? rows[0].Geometry ?? rows[0].the_geom;
          // eslint-disable-next-line no-console
          console.log("[RoutesMapUS] valeur géométrie brute (type=" + typeof rawGeom + ") :", rawGeom);
        } else {
          // eslint-disable-next-line no-console
          console.log("[RoutesMapUS] 0 ligne renvoyée par fetchUSRoutes() pour ces filtres.");
        }

        let drawn = 0;
        let parseFailed = 0;
        rows.forEach((row) => {
          const geo = getRouteGeometry(row);
          if (!geo) {
            parseFailed++;
            return;
          }
          try {
            const color = "#99D6EA";
            const gj = L.geoJSON(geo, {
              style: { color, weight: 1.6, opacity: 0.75 },
            });
            
           gj.bindTooltip(
  `<b>${row.o_iata || "?"} → ${row.d_iata || "?"}</b><br/>
   ${row.distance_nm || "?"} NM `,
  { sticky: true, className: "routes-map-tip" }
);
            gj.addTo(layer);
            drawn += 1;
          } catch (err) {
            parseFailed++;
            // eslint-disable-next-line no-console
            console.warn("[RoutesMapUS] géométrie invalide pour cette ligne, ignorée :", row, err);
          }
        });
        if (parseFailed > 0) {
          // eslint-disable-next-line no-console
          console.warn(`[RoutesMapUS] ${parseFailed}/${rows.length} lignes avaient une géométrie non reconnue.`);
        }
        setLoading(false);
        onStats?.({ routeCount: drawn, loading: false });
      })
      .catch((e) => {
        setError(e.message || "Error loading routes data");
        setLoading(false);
        onStats?.({ routeCount: 0, loading: false });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

return (
  <>
    <div className="routes-map-us">
      <div id="routes-map-us" className="routes-map-us-canvas" />

      <MapLoadingOverlay
        loading={loading}
        label="US routes evolution"
      />

      {error && (
        <div className="routes-map-us-error">
          {error}
        </div>
      )}
    </div>

    <div className="routes-map-us-zoom">
      <button
        title="Zoom in"
        onClick={() => mapRef.current?.zoomIn()}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2.5"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
      </button>

      <button
        title="Zoom out"
        onClick={() => mapRef.current?.zoomOut()}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2.5"
        >
          <path d="M5 12h14" />
        </svg>
      </button>

      <button
        title="Reset view"
        onClick={() =>
          mapRef.current?.setView(
            DEFAULT_CENTER,
            DEFAULT_ZOOM,
            { animate: true }
          )
        }
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2"
        >
          <path d="M3 12a9 9 0 109-9 9.75 9.75 0 00-6.74 2.74L3 8" />
          <path d="M3 3v5h5" />
        </svg>
      </button>
    </div>
  </>
);
}

// Réexport pour que StoryUS puisse charger la liste des statuts sans
// import supplémentaire.
export { fetchUSRouteStatuses };
